/**
 * IntimacyExplanation - Collapsible banner explaining intimacy concepts
 *
 * Provides contextual education about POV intimacy progression or foundation questions.
 */

import { useState } from 'react'
import type { IntimacyExplanationProps } from './types'

export function IntimacyExplanation({
  title,
  description,
  defaultExpanded = false,
}: IntimacyExplanationProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div
      data-testid="intimacy-explanation-banner"
      className="mb-6 overflow-hidden rounded-lg border border-white/20 bg-gradient-to-r from-purple-500/10 to-indigo-500/10"
    >
      {/* Header - always visible */}
      <button
        data-testid="intimacy-explanation-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse explanation' : 'Expand explanation'}
      >
        <h3 className="text-sm font-medium text-white">{title}</h3>
        <svg
          className={`w-5 h-5 text-gray-300 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-3 pt-1 border-t border-white/10">
          <p
            data-testid="intimacy-explanation-description"
            className="text-sm text-gray-300 leading-relaxed"
          >
            {description}
          </p>
        </div>
      )}
    </div>
  )
}
